'use server'

import { apiRequest, ApiResponse } from '../request'
import { getClient } from '@/lib/api/client'
import { downloadBlob } from '../../download-blob'

export enum CertificateType {
    ICP_BRASIL = 'ICP_BRASIL',
    CFM = 'CFM',
}

export enum CertificateStatus {
    CERTIFICATE_ACTIVE = 'CERTIFICATE_ACTIVE',
    CERTIFICATE_EXPIRED = 'CERTIFICATE_EXPIRED',
    CERTIFICATE_REVOKED = 'CERTIFICATE_REVOKED',
    CERTIFICATE_SUSPENDED = 'CERTIFICATE_SUSPENDED',
}

export interface DigitalCertificate {
    id: number
    professionalId: number
    type: CertificateType
    serialNumber: string
    issuer: string
    subject: string
    cpf: string
    crm?: string
    crmState?: string
    validFrom: string
    validUntil: string
    status: CertificateStatus
    fingerprintSha256?: string
    createdAt: string
    updatedAt: string
    professional?: {
        id: number
        name: string
        registrationNumber: string
        councilName: string
    }
}

export interface SignatureLog {
    id: number
    professionalId: number
    documentType: string
    documentId: number
    signedAt: string
    hashAlgorithm: string
    documentHash: string
    signatureValue: string
    certificateSerial: string
    valid: boolean
    validatedAt?: string
    professional?: {
        id: number
        name: string
        registrationNumber: string
        councilName?: string
    }
}

export interface SignPdfRequest {
    documentType: string
    documentId: number
    certificatePassword: string
    companyId: number
}

export interface ExportPdfRequest {
    documentType: string
    documentId: number
    companyId: number
}

export interface SignPdfResponse {
    signedPdfUrl: string
    signatureLog: SignatureLog
    certificate: {
        serialNumber: string
        issuer: string
        subject: string
        validFrom: string
        validUntil: string
    }
}

export interface PublicSignatureVerification {
    valid: boolean
    signedAt: string
    documentType: string
    documentId: number
    hashAlgorithm: string
    documentHash: string
    professional: {
        id: number
        name: string
        registrationNumber: string
        councilName?: string
    }
    company: {
        id: number
        name: string
        tradingName?: string
    }
    certificate: {
        serialNumber: string
        issuer: string
        type: string
        crm?: string
        crmState?: string
        validFrom: string
        validUntil: string
        expired: boolean
        revoked: boolean
        fingerprintSha256?: string
    } | null
    /** Só na verificação pelo código impresso no documento. */
    accessCode?: string
    /** PDF assinado; só na verificação pelo código e com assinatura válida. */
    documentUrl?: string | null
}

// ==================== CERTIFICADOS ====================

export async function getCertificatesByProfessional(
    professionalId: number
): Promise<ApiResponse<DigitalCertificate[]>> {
    return apiRequest<DigitalCertificate[]>(
        `signatures/certificates/professional/${professionalId}`,
        {}
    )
}

export async function getActiveCertificate(
    professionalId: number
): Promise<ApiResponse<DigitalCertificate>> {
    return apiRequest<DigitalCertificate>(
        `signatures/certificates/professional/${professionalId}/active`,
        {}
    )
}

export async function getCertificateById(
    id: number
): Promise<ApiResponse<DigitalCertificate>> {
    return apiRequest<DigitalCertificate>(
        `signatures/certificates/${id}`,
        {}
    )
}

export async function uploadCertificateA1(
    professionalId: number,
    file: File,
    password: string
): Promise<ApiResponse<DigitalCertificate>> {
    const client = await getClient(true)
    const formData = new FormData()
    formData.append('file', file)
    formData.append('professionalId', String(professionalId))
    formData.append('password', password)

    const response = await client.post(
        '/signatures/certificates/upload',
        formData
    )
    return {
        success: true,
        result: response.data,
    }
}

export async function updateCertificateStatus(
    id: number,
    status: CertificateStatus
): Promise<ApiResponse<DigitalCertificate>> {
    return apiRequest<DigitalCertificate>(
        `signatures/certificates/${id}/status`,
        { data: { status } },
        'patch'
    )
}

export async function revokeCertificate(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `signatures/certificates/${id}`,
        {},
        'delete'
    )
}

// ==================== ASSINATURA ====================

export async function signDocumentPdf(
    data: SignPdfRequest
): Promise<ApiResponse<SignPdfResponse>> {
    return apiRequest<SignPdfResponse>(
        'signatures/sign-pdf',
        { data },
        'post'
    )
}

export async function exportDocumentPdf(data: ExportPdfRequest): Promise<void> {
    const client = await getClient(true)
    const response = await client.post('signatures/export-pdf', data, {
        responseType: 'blob',
    })

    const blob = new Blob([response.data], { type: 'application/pdf' })
    const filename = `${data.documentType.toLowerCase()}-${data.documentId}.pdf`

    downloadBlob(blob, filename)
}

export async function signDocument(data: {
    professionalId: number
    documentType: string
    documentId: number
    documentHash: string
    companyId: number
}): Promise<ApiResponse<{ signatureLog: SignatureLog }>> {
    return apiRequest<{ signatureLog: SignatureLog }>(
        'signatures/sign',
        { data },
        'post'
    )
}

// ==================== VERIFICAÇÃO ====================

export async function validateSignature(
    signatureId: number
): Promise<ApiResponse<any>> {
    return apiRequest<any>(
        `signatures/validate/${signatureId}`,
        {},
        'post'
    )
}

export async function validateDocumentSignature(data: {
    documentType: string
    documentId: number
    documentHash: string
}): Promise<ApiResponse<any>> {
    return apiRequest<any>(
        'signatures/validate',
        { data },
        'post'
    )
}

export async function verifySignaturePublic(
    signatureId: number
): Promise<ApiResponse<PublicSignatureVerification>> {
    return apiRequest<PublicSignatureVerification>(
        `signatures/verify/${signatureId}`,
        {}
    )
}

export async function verifySignatureByAccessCode(
    accessCode: string
): Promise<ApiResponse<PublicSignatureVerification>> {
    return apiRequest<PublicSignatureVerification>(
        `signatures/verify/code/${encodeURIComponent(accessCode)}`,
        {}
    )
}

// ==================== LOGS ====================

export async function getSignatureLogsByDocument(
    documentType: string,
    documentId: number
): Promise<ApiResponse<SignatureLog[]>> {
    return apiRequest<SignatureLog[]>(
        `signatures/logs/${documentType}/${documentId}`,
        {}
    )
}

export async function getSignatureLogsByProfessional(
    professionalId: number,
    companyId?: number
): Promise<ApiResponse<SignatureLog[]>> {
    const params: Record<string, any> = {}
    if (companyId) {
        params.companyId = companyId
    }
    return apiRequest<SignatureLog[]>(
        `signatures/logs/professional/${professionalId}`,
        { params }
    )
}

// ==================== MANUTENÇÃO ====================

export async function checkExpiredCertificates(): Promise<
    ApiResponse<{ processed: number }>
> {
    return apiRequest<{ processed: number }>(
        'signatures/maintenance/check-expired',
        {},
        'post'
    )
}
